import { createResource, createSignal, For, Show } from "solid-js";

type AdminToken = {
  id: string;
  email: string;
  display_token: string;
  stripe_customer_id: string | null;
  stripe_checkout_session_id: string | null;
  is_admin: boolean;
  created_at: string | null;
  revoked_at: string | null;
};

async function fetchTokens(): Promise<AdminToken[]> {
  const res = await fetch("/api/admin/tokens", { credentials: "same-origin" });
  if (res.status === 401 || res.status === 403) {
    throw new Error("forbidden");
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return json.items;
}

export default function Admin() {
  const [tokens, { refetch }] = createResource(fetchTokens);
  const [acting, setActing] = createSignal<string | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  const revoke = async (id: string, email: string) => {
    if (!confirm(`Revoke access for ${email}? Their sessions will be killed immediately.`)) return;
    setActing(id);
    setError(null);
    try {
      const res = await fetch(`/api/admin/tokens/${encodeURIComponent(id)}/revoke`, {
        method: "POST",
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : "revoke failed");
    } finally {
      setActing(null);
    }
  };

  return (
    <section class="admin-page">
      <div class="admin-head">
        <h2>Admin · tokens</h2>
        <Show when={tokens()}>
          {(items) => (
            <span class="sub">
              {items().length} token{items().length === 1 ? "" : "s"} ·{" "}
              {items().filter((t) => !t.revoked_at).length} active
            </span>
          )}
        </Show>
      </div>

      <Show when={error()}>
        <div class="admin-error">{error()}</div>
      </Show>

      <Show when={tokens.error}>
        <div class="admin-error">
          {String(tokens.error)} — sign in via the admin tab on /login.
        </div>
      </Show>

      <Show when={tokens()} fallback={<div class="loading">loading tokens...</div>}>
        {(items) => (
          <div class="admin-table">
            <div class="admin-row admin-head-row">
              <div>email</div>
              <div>reference</div>
              <div>stripe</div>
              <div>created</div>
              <div>state</div>
              <div></div>
            </div>
            <For each={items()}>
              {(t) => (
                <div class="admin-row">
                  <div class="admin-email">{t.email}</div>
                  <code class="admin-code">{t.display_token}</code>
                  <code class="admin-code admin-dim">{t.stripe_checkout_session_id ?? "—"}</code>
                  <div class="admin-when">
                    {t.created_at ? new Date(t.created_at).toISOString().slice(0, 10) : "—"}
                  </div>
                  <div>
                    <Show
                      when={!t.revoked_at}
                      fallback={<span class="admin-tag revoked">revoked</span>}
                    >
                      <span class="admin-tag active">active</span>
                    </Show>
                  </div>
                  <div class="admin-actions">
                    <Show when={!t.revoked_at}>
                      <button
                        type="button"
                        class="admin-btn"
                        disabled={acting() === t.id}
                        onClick={() => revoke(t.id, t.email)}
                      >
                        {acting() === t.id ? "..." : "revoke"}
                      </button>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>
        )}
      </Show>
    </section>
  );
}
